import React, { useState } from "react";
import {
  Send,
  Download,
  CheckCircle,
  XCircle,
  MessageSquare,
  BarChart3,
  Eye,
  Trash2,
  Users,
  X,
  Calendar,
  Smartphone,
  Clock,
  PlayCircle,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import {
  EMPTY_CAMPAIGN_STATS,
  getStatusTooltipContent,
  canResumeCampaign,
} from "../../utils/campaignList";

const statusChip = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "completed":
      return "g";
    case "active":
      return "b";
    case "scheduled":
      return "p";
    case "preparing":
    case "pending":
      return "a";
    case "cancelled":
    case "aborted":
    case "failed":
      return "r";
    default:
      return "n";
  }
};

const CampaignCard = ({
  campaign,
  onRetarget,
  onViewAnalytics,
  onAbort,
  onResume,
  onDelete,
  onDownload,
  onUpdateSchedule,
  onCancelScheduled,
  onReschedule,
  onChangeAccount,
  onRefreshStats,
}) => {
  const [refreshingStats, setRefreshingStats] = useState(false);
  const stats = campaign.stats || EMPTY_CAMPAIGN_STATS;
  const showResume = canResumeCampaign(campaign.status);
  const isPreparing = String(campaign.status || "").toLowerCase() === "preparing";
  const prepPercent =
    campaign.preparation_percent != null
      ? campaign.preparation_percent
      : isPreparing && campaign.preparation_total > 0
        ? Math.min(
            100,
            Math.floor(
              ((Number(campaign.prepared_count) || 0) / Number(campaign.preparation_total)) * 100
            )
          )
        : null;

  const handleRefreshStats = async () => {
    if (!onRefreshStats || refreshingStats) return;
    setRefreshingStats(true);
    try {
      await onRefreshStats(campaign.campaign_id);
    } finally {
      setRefreshingStats(false);
    }
  };

  const metrics = [
    { key: "sent", label: "Sent", value: stats.sent, tone: "b", Icon: Send, filter: "sent" },
    { key: "delivered", label: "Delivered", value: stats.delivered, tone: "g", Icon: CheckCircle, filter: "delivered" },
    { key: "failed", label: "Failed", value: stats.failed, tone: "r", Icon: XCircle, filter: "failed" },
    { key: "read", label: "Read", value: stats.read, tone: "p", Icon: Eye, filter: "read" },
    { key: "replies", label: "Replies", value: stats.replies, tone: "a", Icon: MessageSquare, filter: null },
    { key: "pending", label: "Pending", value: stats.pending, tone: "a", Icon: Clock, filter: "pending" },
    { key: "total", label: "Total", value: stats.total, tone: "n", Icon: Users, filter: "all" },
  ];

  return (
    <article className="camp-card">
      <div className="camp-card-top">
        <div className="camp-card-title">
          <h3>{campaign.campaign_name}</h3>
          <span
            className={`ping-chip ${statusChip(campaign.status)}`}
            title={getStatusTooltipContent(campaign)}
          >
            {String(campaign.status || "").replace(/_/g, " ")}
          </span>
        </div>
        <div className="camp-meta">
          {campaign.segment_name && (
            <span><Users size={11} />{campaign.segment_name}</span>
          )}
          <span><MessageSquare size={11} />{campaign.template_name || "N/A"}</span>
          {campaign.phone_number && (
            <span><Smartphone size={11} />{campaign.phone_number}</span>
          )}
        </div>
      </div>

      {isPreparing && (
        <div className="camp-prep">
          <div className="camp-prep-row">
            <span><RefreshCw size={12} className="animate-spin" /> Preparing audience</span>
            <b>
              {(campaign.prepared_count || 0).toLocaleString()}
              {campaign.preparation_total != null
                ? ` / ${Number(campaign.preparation_total).toLocaleString()}`
                : ""}
            </b>
          </div>
          <div className="camp-prep-bar">
            <i style={{ width: `${prepPercent != null ? prepPercent : 8}%` }} />
          </div>
          {prepPercent != null && <em>{prepPercent}% complete</em>}
        </div>
      )}

      <div className="camp-metrics">
        {metrics.map((m) => {
          const inner = (
            <>
              <span className="k"><m.Icon size={11} />{m.label}</span>
              <span className="v">{m.value}</span>
            </>
          );
          if (m.filter == null) {
            return (
              <div key={m.key} className={`camp-metric ${m.tone}`}>
                {inner}
              </div>
            );
          }
          return (
            <button
              key={m.key}
              type="button"
              className={`camp-metric ${m.tone}`}
              onClick={() => onRetarget(campaign, m.filter)}
              title={`Retarget ${m.label.toLowerCase()} contacts`}
            >
              {inner}
            </button>
          );
        })}
      </div>

      <div className="camp-rates">
        <div>
          <span>Success</span>
          <b className="g">{stats.success_pct.toFixed(1)}%</b>
        </div>
        <div>
          <span>Delivery</span>
          <b className="b">{stats.delivery_pct.toFixed(1)}%</b>
        </div>
        <div>
          <span>Failure</span>
          <b className="r">{stats.failure_pct.toFixed(1)}%</b>
        </div>
      </div>

      {(campaign.scheduled_time_original || campaign.scheduled_at) && (
        <div className="camp-note">
          <Calendar size={12} />
          <span>
            {campaign.scheduled_time_original ||
              new Date(campaign.scheduled_at).toLocaleString()}
            {campaign.timezone ? ` (${campaign.timezone})` : ""}
          </span>
        </div>
      )}

      {campaign.error_message && (
        <div className="camp-note r" role="alert">
          <AlertTriangle size={12} />
          <span>{campaign.error_message}</span>
        </div>
      )}

      {stats.pending > 0 && (
        <div className="camp-note a">
          <Clock size={12} />
          <span>Pending: {stats.pending}</span>
        </div>
      )}

      <div className="camp-foot">
        <time>{new Date(campaign.created_at).toLocaleDateString()}</time>
        <div className="camp-actions">
          <button
            type="button"
            onClick={handleRefreshStats}
            disabled={refreshingStats}
            className="camp-ico"
            data-tip="Refresh numbers"
            aria-label="Refresh numbers"
          >
            <RefreshCw size={14} className={refreshingStats ? "animate-spin" : ""} />
          </button>
          <button
            type="button"
            onClick={() => onViewAnalytics(campaign)}
            className="camp-ico"
            data-tip="View analytics"
            aria-label="View analytics"
          >
            <BarChart3 size={14} />
          </button>
          {campaign.status === "active" && (
            <>
              <button
                type="button"
                onClick={() => onAbort(campaign)}
                className="camp-ico danger"
                data-tip="Abort"
                aria-label="Abort"
              >
                <X size={14} />
              </button>
              <button
                type="button"
                onClick={() => onDownload(campaign.campaign_id, campaign.campaign_name)}
                className="camp-ico"
                data-tip="Download logs"
                aria-label="Download logs"
              >
                <Download size={14} />
              </button>
            </>
          )}
          {showResume && (
            <>
              <button
                type="button"
                onClick={() => onResume(campaign.campaign_id)}
                className="camp-ico"
                data-tip="Resume campaign"
                aria-label="Resume campaign"
              >
                <PlayCircle size={14} />
              </button>
              <button
                type="button"
                onClick={() => onDelete(campaign.campaign_id)}
                className="camp-ico danger"
                data-tip="Delete"
                aria-label="Delete"
              >
                <Trash2 size={14} />
              </button>
            </>
          )}
          {!showResume && campaign.status !== "active" && (
            <>
              <button
                type="button"
                onClick={() => onDownload(campaign.campaign_id, campaign.campaign_name)}
                className="camp-ico"
                data-tip="Download logs"
                aria-label="Download logs"
              >
                <Download size={14} />
              </button>
              <button
                type="button"
                onClick={() => onDelete(campaign.campaign_id)}
                className="camp-ico danger"
                data-tip="Delete"
                aria-label="Delete"
              >
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
      </div>

      {(campaign.status === "scheduled" ||
        ((campaign.status === "cancelled" || campaign.status === "aborted") &&
          campaign.scheduled_time_original) ||
        campaign.status === "pending") && (
        <div className="camp-extra">
          {campaign.status === "scheduled" && (
            <>
              <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" onClick={() => onUpdateSchedule(campaign)}>
                <Calendar size={12} />
                Update schedule
              </button>
              <button type="button" className="ping-btn ping-btn-danger ping-btn-sm" onClick={() => onCancelScheduled(campaign.campaign_id)}>
                <X size={12} />
                Cancel
              </button>
            </>
          )}
          {(campaign.status === "cancelled" || campaign.status === "aborted") &&
            campaign.scheduled_time_original && (
              <button type="button" className="ping-btn ping-btn-primary ping-btn-sm" onClick={() => onReschedule(campaign)}>
                <Calendar size={12} />
                Reschedule
              </button>
            )}
          {(campaign.status === "scheduled" ||
            campaign.status === "cancelled" ||
            campaign.status === "aborted" ||
            campaign.status === "pending") && (
            <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" onClick={() => onChangeAccount(campaign)}>
              <Smartphone size={12} />
              Change account
            </button>
          )}
        </div>
      )}
    </article>
  );
};

export default CampaignCard;
